/**
 * AccountDeletionPage.jsx — PUBLIC hesap silme bilgi sayfası
 *
 * Route: /account-deletion
 * Auth: GEREKMEZ — bu sayfa App.js'te ProtectedRoute/RoleRoute'a SARILMAZ,
 * LegalPage.jsx ile aynı standalone iskelet (global Navbar de gizlenir —
 * bkz. App.js isAccountDeletionRoute).
 *
 * MAĞAZA YAYIN KOŞULU: Google Play Data Safety formu, giriş yapmadan
 * erişilebilir bir hesap silme URL'i istiyor (2024'ten itibaren zorunlu):
 *   https://kariyeronline.tr/account-deletion
 *
 * Backend'e bağımlı DEĞİL — içerik tamamen statik i18n
 * (accountDeletion.* anahtarları, LanguageContext.js). Loading/error/empty
 * state gerekmiyor.
 *
 * Dil önceliği: ?lang=tr|en URL parametresi. LegalPage.jsx'te bu parametre
 * sadece backend'den çekilen belge içeriğini etkiler (t() bağımsız olarak
 * global 'lang'e bakmaya devam eder); bu sayfada içeriğin tamamı t() ile
 * geliyor ve t() LanguageContext'te SADECE global lang'e bakabiliyor (ikinci
 * parametre almıyor) — bu yüzden gerçek bir override sağlamak için ?lang=
 * mount'ta global dili toggleLanguage() ile o değere çeker (iki dilli site
 * olduğundan toggle = set). Kullanıcı sonradan header'daki dil düğmelerinden
 * değiştirebilir; bu da global tercihi günceller (localStorage
 * 'preferred_language') — sitenin geri kalanıyla aynı davranış, sayfaya özel
 * ayrı bir dil state'i İCAT EDİLMEDİ.
 *
 * Giriş yapabilenler için Path A → /profile?tab=privacy içindeki
 * DeleteAccountSection.js akışına yönlendirir. Giriş yapamayanlar için
 * Path B → info@kariyeronline.com.tr'ye KVKK madde 11 talebi.
 *
 * ⚠️ İÇERİK DOĞRULUĞU: bu sayfa mağaza incelemecisinin okuduğu beyandır ve
 * backend davranışıyla birebir örtüşmek zorunda — ne fazlası ne eksiği.
 * Öğrenci akışı (DELETE /users/me) hesabı ANINDA pasife alır ve kişisel
 * verileri 72 saat içinde GERÇEKTEN siler. Silme 72 saat gecikmeli olduğu
 * için geçmiş zaman kipi ("silindi") kullanılmaz; "en geç 3 gün içinde
 * silinir" denir.
 * YEDİ bilgi kartı, sırasıyla: erişim biter (accessTitle/Body) → silinen kişisel
 * veriler (deletedTitle/Body) → yasa gereği saklanan mali ve icra kayıtları
 * (retainedTitle/Body) → sözleşme kopyası (contractsTitle/Body) → sertifika ve
 * addaki kamuya açık görünürlük (certificatesTitle/Body) → bakanlıktaki Dijital
 * Rozet kopyası (ministryTitle/Body) → öğrenci öncesi lead kaydı
 * (leadsTitle/Body). İlk ikisi dışındaki BEŞ kart İSTİSNADIR; biri bile
 * çıkarılırsa beyan eksik ifşa olur (mali kayıtlarda ad/adres/TCKN maskelenmiyor;
 * sertifika kaydı süresiz ve girişsiz doğrulanabiliyor; bakanlık kopyası bugün
 * fiilen geri çekilmiyor; lead kaydı silme kapsamı dışında).
 */
import { useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import useReducedMotion from '../hooks/useReducedMotion';
import Logo from '../components/Logo';
import {
  ArrowLeft,
  LogIn,
  UserX,
  Mail,
  Lock,
  Trash2,
  Landmark,
  FileSignature,
  BadgeCheck,
  Building2,
  PhoneCall,
  ShieldCheck,
} from 'lucide-react';

const VALID_LANGS = ['tr', 'en'];
// Kurumsal alan adı .com.tr — .com DEĞİL. KVKK madde 11 talepleri buraya düşer.
const SUPPORT_EMAIL = 'info@kariyeronline.com.tr';

export default function AccountDeletionPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { lang, toggleLanguage, t } = useLanguage();
  const reduced = useReducedMotion();
  const appliedInitialOverride = useRef(false);

  // Mount'ta ?lang= geçerliyse ve global dilden farklıysa siteyi o dile
  // çevirir (bkz. dosya üstü not — t() ikinci parametre almadığı için
  // sayfaya özel bağımsız bir dil override'ı mümkün değil).
  useEffect(() => {
    if (appliedInitialOverride.current) return;
    appliedInitialOverride.current = true;
    const langParam = searchParams.get('lang');
    if (VALID_LANGS.includes(langParam) && langParam !== lang) {
      toggleLanguage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.title = t('accountDeletion.meta.title');
    return () => {
      document.title = 'Kariyer Online';
    };
  }, [t]);

  const switchLang = (nextLang) => {
    if (nextLang !== lang) toggleLanguage();
    const next = new URLSearchParams(searchParams);
    next.set('lang', nextLang);
    setSearchParams(next, { replace: true });
  };

  const mailtoLabel = lang === 'tr'
    ? `${SUPPORT_EMAIL} adresine e-posta gönder`
    : `Send email to ${SUPPORT_EMAIL}`;

  return (
    <div className="min-h-screen duo-bg flex flex-col" data-testid="account-deletion-page">
      <header className="border-b-2 border-[var(--duo-stroke)] px-4 sm:px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-3">
          <Link to="/" className="inline-flex" data-testid="account-deletion-logo-home-link">
            <Logo size={28} />
          </Link>
          <div className="flex items-center gap-1" role="group" aria-label={t('legal.langToggle.label')}>
            {VALID_LANGS.map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => switchLang(l)}
                data-testid={`account-deletion-lang-toggle-${l}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors ${
                  lang === l
                    ? 'bg-[var(--duo-blue)] text-white'
                    : 'text-[var(--duo-text-soft)] hover:bg-[var(--duo-sky)]'
                }`}
              >
                {t(`legal.langToggle.${l}`)}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="flex-1 px-4 sm:px-6 py-10">
        <motion.div
          className="max-w-3xl mx-auto bg-white rounded-2xl p-6 sm:p-10"
          style={{ border: '2px solid var(--duo-stroke)' }}
          initial={reduced ? false : { opacity: 0, y: 10 }}
          animate={reduced ? undefined : { opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
        >
          <div className="flex items-center gap-2 mb-2 text-[var(--duo-blue)]">
            <ShieldCheck size={20} />
            <span className="text-xs font-bold uppercase tracking-wider">
              {t('accountDeletion.header.title')}
            </span>
          </div>
          <h1 className="font-duo-display text-2xl sm:text-3xl text-[var(--duo-text)] mb-3" data-testid="account-deletion-title">
            {t('accountDeletion.header.title')}
          </h1>
          <p className="text-sm text-[var(--duo-text-soft)] font-medium mb-6">
            {t('accountDeletion.header.subtitle')}
          </p>
          {/* A11y (WCAG 1.4.3): --duo-text-soft (#777) beyaz üzerinde 4.48:1 ile
              eşiğin (4.5:1) kıl payı altında. Silme akışının hukuki beyan
              metinleri --duo-text (#3C3C3C, 10.4:1) ile yazılır. */}
          <p className="text-sm text-[var(--duo-text)] font-medium mb-8">
            {t('accountDeletion.intro')}
          </p>

          {/* Path A — giriş yapabilenler */}
          <section
            className="rounded-2xl p-5 sm:p-6 mb-5"
            style={{ border: '2px solid var(--duo-stroke)' }}
            data-testid="account-deletion-path-a"
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center shrink-0">
                <LogIn size={18} />
              </div>
              <h2 className="font-bold text-base text-[var(--duo-text)]">
                {t('accountDeletion.pathA.title')}
              </h2>
            </div>
            <ol className="list-decimal list-inside space-y-1.5 text-sm text-[var(--duo-text-soft)] font-medium mb-4 pl-1">
              <li>{t('accountDeletion.pathA.step1')}</li>
              <li>{t('accountDeletion.pathA.step2')}</li>
              <li>{t('accountDeletion.pathA.step3')}</li>
              <li>{t('accountDeletion.pathA.step4')}</li>
              <li>{t('accountDeletion.pathA.step5')}</li>
            </ol>
            {/* Google Play, silmenin UYGULAMA İÇİNDEN nasıl yapıldığının da
                yazılmasını şart koşuyor; yukarıdaki adımlar yalnızca web yolu. */}
            <p className="text-sm text-[var(--duo-text)] font-medium mb-4" data-testid="account-deletion-path-a-mobile-note">
              {t('accountDeletion.pathA.mobileNote')}
            </p>
            <Link
              to="/profile?tab=privacy"
              className="btn-duo-secondary inline-flex min-h-[44px]"
              data-testid="account-deletion-path-a-cta"
            >
              {t('accountDeletion.pathA.cta')}
            </Link>
          </section>

          {/* Path B — giriş yapamayanlar */}
          <section
            className="rounded-2xl p-5 sm:p-6 mb-8"
            style={{ border: '2px solid var(--duo-stroke)' }}
            data-testid="account-deletion-path-b"
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                <UserX size={18} />
              </div>
              <h2 className="font-bold text-base text-[var(--duo-text)]">
                {t('accountDeletion.pathB.title')}
              </h2>
            </div>
            <p className="text-sm text-[var(--duo-text-soft)] font-medium mb-3">
              {t('accountDeletion.pathB.body')}
            </p>
            <p className="text-sm font-bold text-[var(--duo-text)] mb-2 flex items-center gap-1.5 flex-wrap">
              <Mail size={15} className="text-[var(--duo-blue)] shrink-0" />
              {t('accountDeletion.pathB.emailLabel')}{' '}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="text-[var(--duo-blue)] underline min-h-[44px] inline-flex items-center"
                aria-label={mailtoLabel}
                data-testid="account-deletion-mailto"
              >
                {SUPPORT_EMAIL}
              </a>
            </p>
            <p className="text-xs text-[var(--duo-text-soft)] font-medium">
              {t('accountDeletion.pathB.sla')}
            </p>
          </section>

          {/* Neyi siliyoruz, neyi neden saklıyoruz */}
          <section data-testid="account-deletion-data">
            <h2 className="font-bold text-base text-[var(--duo-text)] mb-4">
              {t('accountDeletion.data.title')}
            </h2>
            {/* 7 kart, 2 sütun. lg:grid-cols-4 BİLEREK YOK: TR metinler EN'den
                ~%30 uzun, 4 sütunda retainedBody 9-10 satıra iniyor. Kart
                gövdeleri text-sm + --duo-text (WCAG 1.4.3, bkz. intro'daki not).
                Renk ailesi: rose = kaybedilen, emerald = yasal saklama,
                sky = sertifika. Bakanlık kartı KASITLI olarak amber: "bugün
                fiilen yapılmıyor" diyor, yasal-saklama ailesiyle aynı görünmemeli.
                amber-600/amber-50 kontrastı ≈3.4:1, WCAG 1.4.11 non-text eşiği
                3:1 → geçer; ayrıca renk tek ayırt edici değil, farkı başlık da
                taşıyor. Son kart tek başına yarım satır bırakmasın diye
                sm:col-span-2. */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="rounded-xl p-4" style={{ border: '2px solid var(--duo-stroke)' }}>
                <div className="w-8 h-8 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center mb-2">
                  <Lock size={16} />
                </div>
                <h3 className="font-bold text-sm text-[var(--duo-text)] mb-1">
                  {t('accountDeletion.data.accessTitle')}
                </h3>
                <p className="text-sm text-[var(--duo-text)] font-medium">
                  {t('accountDeletion.data.accessBody')}
                </p>
              </div>
              <div className="rounded-xl p-4" style={{ border: '2px solid var(--duo-stroke)' }}>
                <div className="w-8 h-8 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center mb-2">
                  <Trash2 size={16} />
                </div>
                <h3 className="font-bold text-sm text-[var(--duo-text)] mb-1">
                  {t('accountDeletion.data.deletedTitle')}
                </h3>
                <p className="text-sm text-[var(--duo-text)] font-medium">
                  {t('accountDeletion.data.deletedBody')}
                </p>
              </div>
              <div className="rounded-xl p-4" style={{ border: '2px solid var(--duo-stroke)' }}>
                <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center mb-2">
                  <Landmark size={16} />
                </div>
                <h3 className="font-bold text-sm text-[var(--duo-text)] mb-1">
                  {t('accountDeletion.data.retainedTitle')}
                </h3>
                <p className="text-sm text-[var(--duo-text)] font-medium">
                  {t('accountDeletion.data.retainedBody')}
                </p>
              </div>
              <div className="rounded-xl p-4" style={{ border: '2px solid var(--duo-stroke)' }}>
                <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center mb-2">
                  <FileSignature size={16} />
                </div>
                <h3 className="font-bold text-sm text-[var(--duo-text)] mb-1">
                  {t('accountDeletion.data.contractsTitle')}
                </h3>
                <p className="text-sm text-[var(--duo-text)] font-medium">
                  {t('accountDeletion.data.contractsBody')}
                </p>
              </div>
              <div className="rounded-xl p-4" style={{ border: '2px solid var(--duo-stroke)' }}>
                <div className="w-8 h-8 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center mb-2">
                  <BadgeCheck size={16} />
                </div>
                <h3 className="font-bold text-sm text-[var(--duo-text)] mb-1">
                  {t('accountDeletion.data.certificatesTitle')}
                </h3>
                <p className="text-sm text-[var(--duo-text)] font-medium">
                  {t('accountDeletion.data.certificatesBody')}
                </p>
              </div>
              <div className="rounded-xl p-4" style={{ border: '2px solid var(--duo-stroke)' }}>
                <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center mb-2">
                  <Building2 size={16} />
                </div>
                <h3 className="font-bold text-sm text-[var(--duo-text)] mb-1">
                  {t('accountDeletion.data.ministryTitle')}
                </h3>
                <p className="text-sm text-[var(--duo-text)] font-medium">
                  {t('accountDeletion.data.ministryBody')}
                </p>
              </div>
              <div className="rounded-xl p-4 sm:col-span-2" style={{ border: '2px solid var(--duo-stroke)' }}>
                <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center mb-2">
                  <PhoneCall size={16} />
                </div>
                <h3 className="font-bold text-sm text-[var(--duo-text)] mb-1">
                  {t('accountDeletion.data.leadsTitle')}
                </h3>
                <p className="text-sm text-[var(--duo-text)] font-medium">
                  {t('accountDeletion.data.leadsBody')}
                </p>
              </div>
            </div>
          </section>
        </motion.div>

        <div className="max-w-3xl mx-auto mt-6 text-center space-y-2">
          <p className="text-xs text-[var(--duo-text-soft)] font-medium">
            {t('accountDeletion.footer.entity')}
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Link to="/" className="text-sm font-bold text-[var(--duo-blue)] hover:underline inline-flex items-center gap-1.5" data-testid="account-deletion-back-home">
              <ArrowLeft size={14} />
              {t('accountDeletion.footer.backHome')}
            </Link>
            <Link to="/login" className="text-sm font-bold text-[var(--duo-blue)] hover:underline inline-flex items-center gap-1.5" data-testid="account-deletion-back-login">
              <LogIn size={14} />
              {t('accountDeletion.footer.backLogin')}
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
